import { useState } from "react";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAcknowledgeAlarm } from "@/features/alarms/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";

// Minimal target — qualquer fonte com id/título/ação serve (AlarmSummary estrutural
// ou a ocorrência da visão de atenção).
export interface AckAlarmTarget {
  id: string;
  title: string;
  expectedAction: string;
}

interface Props {
  alarm: AckAlarmTarget | null;
  open: boolean;
  onClose: () => void;
}

export function AckAlarmInline({ alarm, open, onClose }: Props) {
  const [cause, setCause] = useState("");
  const ack = useAcknowledgeAlarm();

  if (!open || !alarm) return null;

  async function submit() {
    if (cause.trim().length === 0) {
      toast.error("Causa é obrigatória.");
      return;
    }
    try {
      await ack.mutateAsync({ id: alarm!.id, data: { cause: cause.trim() } });
      toast.success("Alarme reconhecido.");
      setCause("");
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao reconhecer.", { description: msg });
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- card clicavel para expandir; foco vai para input interno
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <div>
            <h2 className="text-base font-semibold">Reconhecer alarme</h2>
            <p className="text-xs text-muted-foreground">{alarm.title}</p>
          </div>
          <div className="rounded-md border border-info/30 bg-info/5 p-2 text-xs">
            <strong>Ação esperada:</strong> {alarm.expectedAction}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ack-cause">Causa (obrigatório)</Label>
            <Input
              id="ack-cause"
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              placeholder="Ex: Falha conhecida no PLC, intervenção em andamento."
              maxLength={500}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input ao expandir o ack inline eh foco esperado
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} disabled={ack.isPending}>
              {ack.isPending ? "Reconhecendo…" : "Reconhecer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
